import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getTankerPricing, updateTankerPricing } from '../../../models/tankerModel';

function TankerPricing() {
  const { user } = useAuth();
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const data = await getTankerPricing();
      if (data.status === 1) {
        // Handle both array and single value responses for backward compatibility
        let klArray = [];
        let pricesArray = [];
        
        if (data.data?.tanker_kl) {
          klArray = Array.isArray(data.data.tanker_kl) ? data.data.tanker_kl : [data.data.tanker_kl];
        }
        
        if (data.data?.tanker_prices) {
          pricesArray = Array.isArray(data.data.tanker_prices) ? data.data.tanker_prices : [data.data.tanker_prices];
        }
        
        const tiers = [];
        const maxLength = Math.max(klArray.length, pricesArray.length);
        
        for (let i = 0; i < maxLength; i++) {
          tiers.push({
            id: Date.now() + i,
            kl: klArray[i] || '',
            price: pricesArray[i] || ''
          });
        }
        
        // If no data, start with one empty tier
        if (tiers.length === 0) {
          tiers.push({ id: Date.now(), kl: '', price: '' });
        }
        
        setPricingTiers(tiers);
      } else {
        setMessage(data.message || 'Failed to load pricing data');
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setMessage('Error loading pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setMessage('');
      
      // Convert pricing tiers back to arrays, filtering out empty values
      const klArray = pricingTiers
        .map(tier => tier.kl)
        .filter(kl => kl !== '' && kl !== null && kl !== undefined);
      const pricesArray = pricingTiers
        .map(tier => tier.price)
        .filter(price => price !== '' && price !== null && price !== undefined);
      
      // Ensure arrays have same length
      const maxLength = Math.max(klArray.length, pricesArray.length);
      const finalKlArray = [];
      const finalPricesArray = [];
      
      for (let i = 0; i < maxLength; i++) {
        if (klArray[i] !== undefined && pricesArray[i] !== undefined) {
          finalKlArray.push(klArray[i]);
          finalPricesArray.push(pricesArray[i]);
        }
      }
      
      const pricingData = {
        tanker_kl: finalKlArray,
        tanker_prices: finalPricesArray
      };
      
      const data = await updateTankerPricing(pricingData);
      if (data.status === 1) {
        setMessage('Pricing updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'Failed to update pricing');
      }
    } catch (error) {
      console.error('Error updating pricing:', error);
      setMessage('Error updating pricing');
    } finally {
      setSaving(false);
    }
  };

  const addPricingTier = () => {
    setPricingTiers(prev => [...prev, { id: Date.now(), kl: '', price: '' }]);
  };

  const removePricingTier = (id) => {
    setPricingTiers(prev => prev.filter(tier => tier.id !== id));
  };

  const updatePricingTier = (id, field, value) => {
    setPricingTiers(prev => prev.map(tier => 
      tier.id === id ? { ...tier, [field]: value } : tier
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading pricing...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Tanker Pricing</h1>
                <p className="text-xs text-gray-500 mb-1">Configure pricing tiers for water tanker services</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {pricingTiers.length} pricing tier{pricingTiers.length !== 1 ? 's' : ''} configured
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.includes('successfully') 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
        <form onSubmit={handleSubmit}>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-center border-b border-gray-300 font-medium text-gray-700">
                    Liters
                  </th>
                  <th className="px-4 py-3 text-center border-b border-gray-300 font-medium text-gray-700">
                    Price
                  </th>
                  <th className="px-4 py-3 text-center border-b border-gray-300 font-medium text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.id} className="border-b border-gray-200">
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center">
                        <input
                          type="number"
                          step="0.1"
                          value={tier.kl}
                          onChange={(e) => updatePricingTier(tier.id, 'kl', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                        <span className="ml-2 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium text-gray-700">
                          kl
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center">
                        <span className="mr-2 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium text-gray-700">
                          R
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={tier.price}
                          onChange={(e) => updatePricingTier(tier.id, 'price', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => removePricingTier(tier.id)}
                        disabled={pricingTiers.length === 1}
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={addPricingTier}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Pricing
            </button>
            
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

export default TankerPricing; 